import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import ExcelJS from "exceljs";
import { toPng } from "html-to-image";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllStory } from "../../pages/api/backlog";
import { getAllSprints } from "../../pages/api/sprint";
import TaskDetails from "../activesprint/taskdetails/page";

/* ── Badge helpers ──────────────────────────────────────────────────────────── */
const PRIORITY_STYLES = {
  HIGH: { background: "#fdecea", color: "#e53935" },
  MEDIUM: { background: "#fff3e0", color: "#fb8c00" },
  LOW: { background: "#e8f5e9", color: "#43a047" },
};

const TYPE_STYLES = {
  feature: { background: "#e3f0ff", color: "#1565c0" },
  bug: { background: "#fdecea", color: "#c62828" },
  task: { background: "#e8f5e9", color: "#2e7d32" },
  enhancement: { background: "#fff3e0", color: "#e65100" },
  report: { background: "#f3e5f5", color: "#6a1b9a" },
};

const getTypeBadgeStyle = (type) =>
  TYPE_STYLES[type?.toLowerCase()] || { background: "#e0e0e0", color: "#555" };

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "--";

const COLUMNS = [
  { status: "TODO", label: "To Do", dot: "#9CA3AF", bg: "#f9fafb" },
  { status: "INPROGRESS", label: "In Development", dot: "#3B82F6", bg: "#eff6ff" },
  { status: "INTESTING", label: "In Testing", dot: "#A855F7", bg: "#faf5ff" },
  { status: "COMPLETED", label: "Completed", dot: "#22C55E", bg: "#f0fdf4" },
];

/* ── Completed-sprints table helpers (employee-scoped) ──────────────────────── */
const isSprintCompleted = (sprint) => {
  if (sprint?.status) return String(sprint.status).toUpperCase() === "COMPLETED";
  if (sprint?.sprintStatus) return String(sprint.sprintStatus).toUpperCase() === "COMPLETED";
  if (sprint?.endDate) return new Date(sprint.endDate) < new Date();
  return false;
};

const achievementColor = (pct) => {
  if (pct >= 90) return { bar: "#22C55E", text: "#16A34A" };
  if (pct >= 75) return { bar: "#F59E0B", text: "#B45309" };
  return { bar: "#EF4444", text: "#DC2626" };
};

// Only stories where the logged-in employee is one of the assigned storyUsers.
// AFTER
const storiesForEmployee = (sprintStories, employeeId) =>
  sprintStories.filter((s) =>
    (s.storyUsers || []).some((su) => String(su.user?.id) === String(employeeId))
  );

// Picks the correct SP field based on the employee's role on that specific story —
// DEVELOPER → storyPoints (Dev SP), TESTER → testStoryPoints (Test SP)
const getMySP = (story, employeeId) => {
  const myRole = (story.storyUsers || []).find(
    (su) => String(su.user?.id) === String(employeeId)
  )?.userRole;
  return myRole === "TESTER"
    ? Number(story.testStoryPoints) || 0
    : Number(story.storyPoints) || 0;
};

// ✅ Employee-scoped rows: Target SP / Achieved SP reflect ONLY the
// logged-in employee's own assigned stories in that sprint — NOT the
// sprint-wide totals shown on the admin table (those cover every resource
// on the sprint, which isn't what an individual employee should see here).
const buildSprintRows = (sprints, allStories, employeeId) => {
  return sprints
    .filter(isSprintCompleted)
    .map((sprint) => {
      const sprintStories = allStories.filter((s) => s.sprint?.id === sprint.id);
      const myStories = storiesForEmployee(sprintStories, employeeId);

      // ✅ Prefer this employee's snapshot captured when the sprint was
      // stopped (before incomplete stories were moved out) — same fix as
      // the admin table's sprint-wide snapshot, just scoped to this user.
      const mySnapshot = (sprint.userSnapshots || []).find(
        (us) => String(us.user?.id) === String(employeeId)
      );

      // only include sprints this employee was actually tagged in —
      // via a live story assignment OR a saved per-user snapshot
      const wasTagged = myStories.length > 0 || !!mySnapshot;
      if (!wasTagged) return null;

     const targetSP = mySnapshot
  ? (mySnapshot.targetSP || 0)
  : myStories.reduce((sum, s) => sum + getMySP(s, employeeId), 0);
const achievedSP = mySnapshot
  ? (mySnapshot.achievedSP || 0)
  : myStories.filter((s) => s.status === "COMPLETED").reduce((sum, s) => sum + getMySP(s, employeeId), 0);
      const achievement = targetSP > 0 ? Math.round((achievedSP / targetSP) * 100) : 0;

      return {
        id: sprint.id,
        sprintName: sprint.sprintName,
        startDate: sprint.startDate,
        endDate: sprint.endDate,
        targetSP,
        achievedSP,
        achievement,
        raw: sprint,
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate));
};

/* ── Sprint Details modal data builder (employee-scoped) ────────────────────── */
const buildSprintDetails = (sprint, allStories, employeeId) => {
  const sprintStories = allStories.filter((s) => s.sprint?.id === sprint.id);
  const myStories = storiesForEmployee(sprintStories, employeeId);

  const mySnapshot = (sprint.userSnapshots || []).find(
    (us) => String(us.user?.id) === String(employeeId)
  );

const targetSP = mySnapshot
  ? (mySnapshot.targetSP || 0)
  : myStories.reduce((sum, s) => sum + getMySP(s, employeeId), 0);
const achievedSP = mySnapshot
  ? (mySnapshot.achievedSP || 0)
  : myStories.filter((s) => s.status === "COMPLETED").reduce((sum, s) => sum + getMySP(s, employeeId), 0);
const achievement = targetSP > 0 ? Math.round((achievedSP / targetSP) * 100) : 0;

  const totalTasks = mySnapshot ? (mySnapshot.totalTasks || 0) : myStories.length;
  const completedTasks = mySnapshot ? (mySnapshot.completedTasks || 0) : myStories.filter((s) => s.status === "COMPLETED").length;
  const carriedFwd = Math.max(totalTasks - completedTasks, 0);

  return {
    sprintName: sprint.sprintName,
    startDate: sprint.startDate,
    endDate: sprint.endDate,
    targetSP,
    achievedSP,
    achievement,
    status: isSprintCompleted(sprint) ? "Completed" : "In Progress",
    totalTasks,
    completedTasks,
    carriedFwd,
  };
};

/* ── Stat card (table view) ───────────────────────────────────────────────── */
const StatCard = ({ label, value, valueColor, subtitle, accent }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderLeft: `4px solid ${accent || "#2563EB"}`,
      borderRadius: 10,
      padding: "18px 20px",
      flex: 1,
    }}
  >
    <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: valueColor || "#111827", lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 8 }}>{subtitle}</div>
  </div>
);

/* ── Sprint Details Modal (employee-scoped, no Resources section) ───────────── */
const SprintDetailsModal = ({ show, onClose, details, onExport, onSnapshot }) => {
  const captureRef = useRef(null);
  const [navbarHeight, setNavbarHeight] = useState(64);

  useEffect(() => {
    if (!show) return;
    const navEl = document.querySelector("header") || document.querySelector(".navbar");
    if (navEl) setNavbarHeight(navEl.getBoundingClientRect().height);
  }, [show]);
  if (!show || !details) return null;
  const colors = achievementColor(details.achievement);
  return createPortal(
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", justifyContent: "flex-end", zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        ref={captureRef}
        onClick={(e) => e.stopPropagation()}
        style={{ width: 380, maxWidth: "100%", height: "100%", background: "#fff", boxShadow: "-4px 0 20px rgba(0,0,0,0.1)", overflowY: "auto", padding: `${navbarHeight + 24}px 24px 24px`, display: "flex", flexDirection: "column", gap: 20 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>Sprint Details</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#6B7280" }}>×</button>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", marginBottom: 10 }}>SPRINT INFORMATION</div>
          <div style={{ background: "#F9FAFB", border: "1px solid #F3F4F6", borderRadius: 8, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              ["Sprint Name", details.sprintName],
              ["Duration", `${formatDate(details.startDate)} – ${formatDate(details.endDate)}`],
              ["Start Date", formatDate(details.startDate)],
              ["End Date", formatDate(details.endDate)],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#6B7280" }}>{label}</span>
                <span style={{ color: "#111827", fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", marginBottom: 10 }}>PERFORMANCE SUMMARY</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>Target SP</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{details.targetSP}</div>
            </div>
            <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>Achieved SP</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{details.achievedSP}</div>
            </div>
            <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>Achievement</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{details.achievement}%</div>
            </div>
            <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>Status</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#16A34A" }}>{details.status}</div>
            </div>
          </div>
          <div style={{ height: 6, borderRadius: 4, background: "#F3F4F6", overflow: "hidden" }}>
            <div style={{ width: `${Math.min(details.achievement, 100)}%`, height: "100%", background: colors.bar }} />
          </div>
        </div>

        <div data-snapshot-exclude="true" style={{ display: "flex", gap: 10, marginTop: "auto", paddingTop: 10 }}>
          <button
            onClick={() => onSnapshot(captureRef.current, details.sprintName)}
            style={{ flex: 1, padding: "10px 0", border: "1px solid #E5E7EB", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
          >
            <i className="bi bi-camera" /> Sprint Snapshot
          </button>
          <button onClick={onExport} style={{ flex: 1, padding: "10px 0", border: "none", borderRadius: 8, background: "#2563EB", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <i className="bi bi-file-earmark-text" /> Export Report
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
/* ── Progress Bar ───────────────────────────────────────────────────────────── */
const SprintProgressBar = ({ progress = 0 }) => (
  <div className="as-progress-wrap">
    <div className="flex-between txt-12 mb-1">
      <span className="as-progress-label">Sprint progress</span>
      <span className="as-progress-pct">{progress}%</span>
    </div>
    <div className="as-progress-track">
      <div className="as-progress-bar" style={{ width: `${progress}%` }} />
    </div>
  </div>
);

/* ── Story card ─────────────────────────────────────────────────────────────── */
const StoryCard = ({ story, onView }) => (
  <>
    <div className="as-card-top-row">
      <span className="as-card-id">#{story.id}</span>
      <div className="as-card-badges">
        {story.storyType && (
          <span className="badge-base" style={getTypeBadgeStyle(story.storyType)}>
            {story.storyType.charAt(0) + story.storyType.slice(1).toLowerCase()}
          </span>
        )}
      </div>
    </div>

    <div className="as-card-title">{story.storyTitle}</div>

    {story.storyDescription && (
      <div className="as-card-desc">
        {story.storyDescription.replace(/[#*_`>\[\]]/g, "").trim()}
      </div>
    )}

    <div className="as-card-bottom-row">
      <div className="as-card-bottom-left">
        {story.priority && (
          <span className="badge-base" style={PRIORITY_STYLES[story.priority] || {}}>
            {story.priority.charAt(0) + story.priority.slice(1).toLowerCase()}
          </span>
        )}
        <span className="issue-card-sp">
          {story.storyPoints ? `${story.storyPoints} SP` : "- SP"}
        </span>
      </div>

      <div className="as-card-bottom-right">
        {story.storyUsers && (
          <div className="issue-card-avatars">
            {story.storyUsers.filter((su) => su.user?.name).map((su) => (
              <OverlayTrigger
                key={su.id}
                placement="top"
                overlay={<Tooltip>{su.user.name}</Tooltip>}
              >
                <div className="issue-avatar">{getInitials(su.user.name)}</div>
              </OverlayTrigger>
            ))}
          </div>
        )}
        {(story.comments?.length > 0 || story.commentCount > 0) && (
          <span className="as-card-comments">
            <i className="bi bi-chat txt-11" />
            {story.comments?.length || story.commentCount}
          </span>
        )}
        <OverlayTrigger placement="top" overlay={<Tooltip>View task</Tooltip>}>
          <button className="issue-card-icon-btn" onClick={() => onView(story)}>
            <i className="bi bi-eye" />
          </button>
        </OverlayTrigger>
      </div>
    </div>

    <div className="as-card-meta-row">
      {story.sprint?.startDate && (
        <span className="as-card-date">
          <i className="bi bi-calendar3 txt-11" />
          {formatDate(story.sprint.startDate)}
        </span>
      )}
      <div className="as-card-meta-right">
        {story.storyModules?.length > 0 && (
          <span className="as-card-meta-item">
            <i className="bi bi-chat txt-11" />
            {story.storyModules.length}
          </span>
        )}
        {story.attachments?.length > 0 && (
          <span className="as-card-meta-item">
            <i className="bi bi-paperclip txt-11" />
            {story.attachments.length}
          </span>
        )}
      </div>
    </div>

    {story.sentForRTS && !story.rtsAccepted && <div className="rts-bar sent">Sent for RTS</div>}
    {story.rtsAccepted && <div className="rts-bar accepted">✓ Accepted</div>}
  </>
);

/* ════════════════════════════════════════════════════════════════════════════
   EMPLOYEE COMPLETED SPRINT
   Read-only view of every story in a completed sprint (same visibility as
   admin's completed sprint board). Uses the employee side's real sprint
   selection (selectedSprint in localStorage + "sprintChanged" event) instead
   of the "completedSprintSelected" event, which is never dispatched here.
════════════════════════════════════════════════════════════════════════════ */
const EmployeeCompletedSprint = () => {
  const [view, setView] = useState("list"); // "list" | "board"
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showViewTaskModal, setShowViewTaskModal] = useState(false);
  const [currentStory, setCurrentStory] = useState(null);
  const [showSprintDetailsModal, setShowSprintDetailsModal] = useState(false);
  const token = localStorage.getItem("token");
  // ✅ Logged-in employee's own id (set on login — see AuthRequest.userId
  // returned by /auth/login). Table/modal numbers below are scoped to this id.
  const employeeId = localStorage.getItem("id");

  /* Table-view state */
  const [allSprints, setAllSprints] = useState([]);
  const [allStories, setAllStories] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  /* Load data for the summary table on mount */
  useEffect(() => {
    const loadTableData = async () => {
      setTableLoading(true);
      try {
        const [sprintRes, storyRes] = await Promise.all([
          getAllSprints(token),
          getAllStory(token),
        ]);
        setAllSprints(sprintRes.data || []);
        setAllStories(storyRes.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setTableLoading(false);
      }
    };
    loadTableData();
  }, [token]);

  /* Listen for completed sprint selection — same as admin side */
  useEffect(() => {
    const handler = (e) => {
      setSelectedSprint(e.detail);
      setView("board");
    };
    window.addEventListener("completedSprintSelected", handler);
    return () => window.removeEventListener("completedSprintSelected", handler);
  }, []);

  /* Fetch stories whenever the selected sprint changes */
  useEffect(() => {
    if (!selectedSprint) return;
    const fetchStories = async () => {
      setLoading(true);
      try {
        const res = await getAllStory(token);
        const filtered = (res.data || []).filter((s) => s.sprint?.id === selectedSprint.id);
        setStories(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, [selectedSprint, token]);

  const handleViewTaskShow = (story) => {
    setCurrentStory({ ...story, attachments: story.attachments || [] });
    setShowViewTaskModal(true);
  };

  const handleViewSprint = (sprint) => {
    setSelectedSprint(sprint);
    setShowSprintDetailsModal(true);
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedSprint(null);
  };

  const completedCount = stories.filter((s) => s.status === "COMPLETED").length;
  const progress = stories.length ? Math.round((completedCount / stories.length) * 100) : 0;

  /* Table rows — employee-scoped */
  const rows = useMemo(() => buildSprintRows(allSprints, allStories, employeeId), [allSprints, allStories, employeeId]);
  const sprintDetails = useMemo(
    () => (selectedSprint ? buildSprintDetails(selectedSprint, allStories, employeeId) : null),
    [selectedSprint, allStories, employeeId]
  );

  const filteredRows = useMemo(() => {
    return rows
      .filter((r) => r.sprintName?.toLowerCase().includes(search.toLowerCase()))
      .filter((r) => {
        if (filter === "high") return r.achievement >= 90;
        if (filter === "low") return r.achievement < 75;
        return true;
      });
  }, [rows, search, filter]);
  useEffect(() => { setPage(1); }, [search, filter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const paginatedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalCompleted = rows.length;

   // ✅ Employee-scoped average: only count sprints where THIS employee
  // actually had story points assigned (targetSP > 0). Each participated
  // sprint contributes its own achievement % equally — sprints the
  // employee had no work in are excluded from both the average and the
  // count, instead of silently netting to 0/0 inside a global SP ratio.
  const participatedRows = rows.filter((r) => r.targetSP > 0);
  const avgAchievement = participatedRows.length > 0
    ? Math.round(
        participatedRows.reduce((sum, r) => sum + r.achievement, 0) / participatedRows.length
      )
    : 0;

  const deliveredSP = rows.reduce((sum, r) => sum + r.achievedSP, 0);
  const totalTargetSP = rows.reduce((sum, r) => sum + r.targetSP, 0);

  const handleExport = () => {
    const header = ["Sprint Name", "Start Date", "End Date", "Target SP", "Achieved SP", "Achievement %"];
    const csvRows = filteredRows.map((r) => [
      r.sprintName,
      formatDate(r.startDate),
      formatDate(r.endDate),
      r.targetSP,
      r.achievedSP,
      r.achievement,
    ]);
    const csv = [header, ...csvRows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-completed-sprints.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSprintSnapshot = async (node, sprintName) => {
    if (!node) return;
    try {
      const actionRow = node.querySelector('[data-snapshot-exclude="true"]');
      if (actionRow) actionRow.style.visibility = "hidden";

      const originalHeight = node.style.height;
      const originalMaxHeight = node.style.maxHeight;
      const originalOverflowY = node.style.overflowY;
      node.style.height = "auto";
      node.style.maxHeight = "none";
      node.style.overflowY = "visible";

      const dataUrl = await toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        width: node.scrollWidth,
        height: node.scrollHeight,
      });

      node.style.height = originalHeight;
      node.style.maxHeight = originalMaxHeight;
      node.style.overflowY = originalOverflowY;
      if (actionRow) actionRow.style.visibility = "visible";

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${sprintName || "sprint"}-snapshot.png`;
      a.click();
    } catch (e) {
      console.error("Snapshot capture failed:", e);
    }
  };

  const handleExportSprintReport = async () => {
    if (!sprintDetails) return;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Sprint Report");
    sheet.columns = [{ width: 22 }, { width: 30 }];

    const sectionHeaderStyle = {
      font: { bold: true, color: { argb: "FFFFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } },
      alignment: { vertical: "middle" },
    };
    const labelStyle = { font: { color: { argb: "FF6B7280" } } };
    const valueStyle = { font: { bold: true, color: { argb: "FF111827" } } };
    const thinBorder = { style: "thin", color: { argb: "FFE5E7EB" } };
    const withBorder = (cell) => {
      cell.border = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
    };

    const addSection = (title, rows) => {
      const headerRow = sheet.addRow([title, ""]);
      sheet.mergeCells(`A${headerRow.number}:B${headerRow.number}`);
      headerRow.eachCell((cell) => { cell.style = sectionHeaderStyle; withBorder(cell); });
      headerRow.height = 20;

      rows.forEach(([label, value]) => {
        const row = sheet.addRow([label, value]);
        row.getCell(1).style = labelStyle;
        row.getCell(2).style = valueStyle;
        withBorder(row.getCell(1));
        withBorder(row.getCell(2));
      });

      sheet.addRow([]);
    };

    const titleRow = sheet.addRow([`${sprintDetails.sprintName} Report`, ""]);
    sheet.mergeCells(`A${titleRow.number}:B${titleRow.number}`);
    titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: "FF111827" } };
    titleRow.height = 24;
    sheet.addRow([]);

    addSection("SPRINT INFORMATION", [
      ["Sprint Name", sprintDetails.sprintName],
      ["Duration", `${formatDate(sprintDetails.startDate)} - ${formatDate(sprintDetails.endDate)}`],
      ["Start Date", formatDate(sprintDetails.startDate)],
      ["End Date", formatDate(sprintDetails.endDate)],
    ]);


    addSection("PERFORMANCE SUMMARY", [
      ["Target SP", sprintDetails.targetSP],
      ["Achieved SP", sprintDetails.achievedSP],
      ["Achievement %", `${sprintDetails.achievement}%`],
      ["Status", sprintDetails.status],
    ]);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sprintDetails.sprintName || "sprint"}-report.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const storiesForColumn = (status) =>
    stories.filter((s) => {
      const effective = ["TODO", "INPROGRESS", "INTESTING", "COMPLETED"].includes(s.status)
        ? s.status
        : "TODO";
      return effective === status;
    });

  /* ════════════════════════ LIST (TABLE) VIEW ═══════════════════════════════ */
  if (view === "list") {
    return (
      <div style={{ fontFamily: "inherit", height: "calc(100vh - 140px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <SprintDetailsModal
          show={showSprintDetailsModal}
          onClose={() => setShowSprintDetailsModal(false)}
          details={sprintDetails}
          onExport={handleExportSprintReport}
          onSnapshot={handleSprintSnapshot}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <h5 className="as-heading page-main-heading" style={{ margin: 0 }}>
            Completed Sprint
          </h5>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative" }}>
              <i
                className="bi bi-search"
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9CA3AF",
                  fontSize: 13,
                }}
              />
              <input
                type="text"
                placeholder="Search sprint"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  padding: "8px 12px 8px 32px",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  fontSize: 13,
                  width: 190,
                  outline: "none",
                  color: "#374151",
                }}
              />
            </div>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                fontSize: 13,
                color: "#374151",
                background: "#fff",
              }}
            >
              <option value="all">All sprints</option>
              <option value="high">High achievement (≥90%)</option>
              <option value="low">Needs improvement (&lt;75%)</option>
            </select>

            <button
              onClick={handleExport}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                fontSize: 13,
                color: "#374151",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              <i className="bi bi-download" />
              Export
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                   <StatCard
            label="My Completed Sprints"
            value={participatedRows.length}
            subtitle="All-time"
            accent="#2563EB"
          />
                    <StatCard
            label="My Average Achievement"
            value={`${avgAchievement}%`}
            valueColor="#F59E0B"
            subtitle={`Across ${participatedRows.length} sprint${participatedRows.length !== 1 ? "s" : ""} you worked on`}
            accent="#F97316"
          />
          <StatCard
            label="My Delivered Story Points"
            value={`${deliveredSP} / ${totalTargetSP} SP`}
            subtitle="Total achieved"
            accent="#10B981"
          />
        </div>

        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px" }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Completed Sprints</span>
            <span style={{ fontSize: 13, color: "#9CA3AF" }}>{filteredRows.length} sprints</span>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead>
                <tr style={{ borderTop: "1px solid #F3F4F6", borderBottom: "1px solid #F3F4F6" }}>
                  {["Sprint Name", "Duration", "Target SP", "Achieved SP", "Achievement", "Actions"].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        textAlign: (i === 2 || i === 3 || i === 5) ? "right" : "left",
                        padding: "12px 20px",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#9CA3AF",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 1,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#9CA3AF" }}>Loading...</td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#9CA3AF" }}>No completed sprints found.</td>
                  </tr>
                ) : (
                  paginatedRows.map((r) => {
                    const colors = achievementColor(r.achievement);
                    return (
                      <tr key={r.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                        <td style={{ padding: "14px 20px", fontWeight: 600, color: "#111827", fontSize: 14 }}>{r.sprintName}</td>
                        <td style={{ padding: "14px 20px", color: "#6B7280", fontSize: 13, whiteSpace: "nowrap" }}>
                          {formatDate(r.startDate)} - {formatDate(r.endDate)}
                        </td>
                        <td style={{ padding: "14px 20px", textAlign: "right", color: "#111827", fontSize: 14 }}>{r.targetSP}</td>
                        <td style={{ padding: "14px 20px", textAlign: "right", color: "#111827", fontSize: 14 }}>{r.achievedSP}</td>
                        <td style={{ padding: "14px 20px", minWidth: 180 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ flex: 1, height: 6, borderRadius: 4, background: "#F3F4F6", overflow: "hidden" }}>
                              <div style={{ width: `${Math.min(r.achievement, 100)}%`, height: "100%", borderRadius: 4, background: colors.bar }} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: colors.text, minWidth: 34 }}>{r.achievement}%</span>
                          </div>
                        </td>
                        <td style={{ padding: "14px 20px", textAlign: "right" }}>
                          <button
                            onClick={() => handleViewSprint(r.raw)}
                            style={{ background: "none", border: "none", color: "#2563EB", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
                          >
                            View <i className="bi bi-chevron-right" style={{ fontSize: 11 }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px", borderTop: "1px solid #F3F4F6", fontSize: 13, color: "#6B7280" }}>
            <span>
              Showing {filteredRows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ border: "1px solid #E5E7EB", background: "#F9FAFB", borderRadius: 6, padding: "2px 10px", cursor: page === 1 ? "not-allowed" : "pointer" }}
              >
                ‹
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{ border: "1px solid #E5E7EB", background: "#F9FAFB", borderRadius: 6, padding: "2px 10px", cursor: page === totalPages ? "not-allowed" : "pointer" }}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════ BOARD (KANBAN) VIEW — unchanged below ═══════════ */
  return (
    <div style={{ fontFamily: "inherit" }}>
      <ToastContainer />

      <TaskDetails
        showViewTaskModal={showViewTaskModal}
        setShowViewTaskModal={setShowViewTaskModal}
        currentStory={currentStory}
        setCurrentStory={setCurrentStory}
        onStoryUpdated={() => {}}
        showAcceptRTS={false}
        readOnly
      />

      <button
        onClick={handleBackToList}
        style={{ background: "none", border: "none", color: "#2563EB", fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
      >
        <i className="bi bi-chevron-left" style={{ fontSize: 11 }} />
        Back to completed sprints
      </button>

      <h5 className="as-heading">Completed Sprint</h5>

      <div className="as-sprint-card flex-between" style={{ gap: 24, marginBottom: 4 }}>
        <div className="as-sprint-left">
          <div className="flex-gap-10" style={{ marginBottom: 4 }}>
            <span className="as-sprint-name">{selectedSprint.sprintName}</span>
            <span
              style={{
                background: "#fef9c3",
                color: "#854d0e",
                fontSize: 12,
                fontWeight: 600,
                padding: "2px 10px",
                borderRadius: 20,
              }}
            >
              Completed
            </span>
          </div>
          <div className="flex-gap-12 txt-12">
            <span className="as-sprint-dates">
              {formatDate(selectedSprint.startDate)} – {formatDate(selectedSprint.endDate)}
            </span>
            <span className="as-sprint-ended">Ended</span>
          </div>
        </div>
        <SprintProgressBar progress={progress} />
      </div>

      <div className="dashboard-container">
        <div className="board-wrap">
          <div className="board-header">
            {COLUMNS.map(({ label, dot, status }) => (
              <div key={status} className="board-header-cell">
                <div className="board-header-left">
                  <span className="board-dot" style={{ background: dot }} />
                  <span className="board-col-title">{label}</span>
                  <span className="board-col-count">{storiesForColumn(status).length}</span>
                </div>
                <button className="board-col-add" style={{ visibility: "hidden" }}>
                  +
                </button>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="as-board-empty">Loading...</div>
          ) : stories.length === 0 ? (
            <div className="as-board-empty">No stories found for this sprint.</div>
          ) : (
            stories.map((story) => {
              const effectiveStatus = ["TODO", "INPROGRESS", "INTESTING", "COMPLETED"].includes(story.status)
                ? story.status
                : "TODO";

              return (
                <div key={story.id} className="board-row">
                  {COLUMNS.map(({ status, bg }) => (
                    <div
                      key={status}
                      className="subtask-col-cell"
                      style={{ background: bg, minHeight: 80, padding: 6, borderRadius: 6 }}
                    >
                      {effectiveStatus === status && (
                        <div
                          style={{
                            background: "#fff",
                            border: "1px solid #E5E7EB",
                            borderRadius: 8,
                            padding: "8px 10px",
                            marginBottom: 8,
                            cursor: "default",
                          }}
                        >
                          <StoryCard story={story} onView={handleViewTaskShow} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeCompletedSprint;
